import { gql, useQuery } from "@apollo/client";
import { useApplicationContext } from "components/Application";
import { CenterSpinner } from "components/CenterSpinner";
import { useFiltersContext } from "components/FiltersContext";
import { Footer } from "components/Footer";
import { ListFilters } from "components/ListFilters";
import { ListItem } from "components/ListItem";
import { capitalize } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import "./Seasons.css";
import "twin.macro";

const collectionSeasonQuery = gql`
  query($seasonId: ID!, $colorGroupsFilter: [ID!], $compositionGroupsFilter: [ID!], $kindGroupsFilter: [ID!]) {
    collectionSeason(id: $seasonId) {
      id
      code
      nameEn
      nameIt
      kindEn
      kindIt
      brand {
        id
        nameEn
        nameIt
      }
      lines {
        id
        code
        color
        coverHorizontal
        coverVertical
        nameEn
        nameIt
        descriptionEn
        descriptionIt
      }
      items(
        colorGroupsFilter: $colorGroupsFilter
        compositionGroupsFilter: $compositionGroupsFilter
        kindGroupsFilter: $kindGroupsFilter
      ) {
        id
        code
        nameEn
        nameIt
        cover
        available
        kind {
          id
          code
        }
        color {
          id
          code
          nameEn
          nameIt
          patch
        }
        sizeGroup {
          id
          code
          sizes {
            id
            code
            value
          }
        }
        season {
          id
          isStock
        }
        line {
          id
          code
          color
        }
        stocks {
          quantity
          size {
            id
            code
          }
        }
        variants {
          id
          available
          color {
            id
            code
            patch
          }
        }
      }
      colorGroups {
        id
        code
        nameEn
        nameIt
        patch
      }
      compositionGroups {
        id
        code
        nameEn
        nameIt
      }
      kindGroups {
        id
        code
        nameEn
        nameIt
      }
    }
  }
`;

export const Show: React.FC<{}> = () => {
  const {
    siteEnvironment: { appEndpointStore },
    session,
  } = useApplicationContext();
  const { filteredColorGroups, filteredCompositionGroups, filteredKindGroups } = useFiltersContext();
  const { currentUser } = session;
  const { priceList } = currentUser;
  const { prices } = priceList;
  const { seasonId } = useParams();

  const { i18n } = useTranslation();
  const languageSuffix = capitalize(i18n.language);

  const variables = {
    seasonId,
    colorGroupsFilter: filteredColorGroups.map((colorGroup) => colorGroup.id),
    compositionGroupsFilter: filteredCompositionGroups.map((compositionGroup) => compositionGroup.id),
    kindGroupsFilter: filteredKindGroups.map((kindGroup) => kindGroup.id),
  };
  const { loading, data } = useQuery(collectionSeasonQuery, { variables, fetchPolicy: "cache-and-network" });

  if (loading) return <CenterSpinner size="large" />;

  const { collectionSeason: season } = data;
  const { brand, lines, items, colorGroups, compositionGroups, kindGroups } = season;

  return (
    <div tw="flex space-x-2">
      <div tw="flex flex-col w-full">
        <ListFilters
          brand={brand}
          season={season}
          colorGroups={colorGroups}
          compositionGroups={compositionGroups}
          kindGroups={kindGroups}
        />

        {lines.length > 1 && (
          <div className="line-block" tw="flex flex-row mx-2 mb-12">
            {lines.map((line: any) => (
              <div
                key={line.code}
                tw="flex border-l-16 w-full lg:h-full bg-cover bg-no-repeat justify-end items-end p-4 m-2"
                style={{
                  backgroundImage: `url(${appEndpointStore}${lines.length > 2 ? line.coverVertical : line.coverHorizontal})`,
                  borderLeftColor: `#${line.color}`,
                }}
              >
                <div tw="flex flex-col justify-between items-end">
                  <Link
                    to={`/b/${brand.id}/s/${season.id}/l/${line.id}`}
                    tw="bg-white w-auto text-center border-default border-gray-900 py-2 px-8 text-gray-900 outline-none hover:(bg-gray-900 text-white)"
                  >
                    {line[`name${languageSuffix}`]}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div tw="grid lg:grid-cols-4 lg:gap-4 lg:mx-4 lg:my-4 grid-cols-3 mx-4 mt-8 gap-4">
            {items.map((item: any) => (
              <ListItem key={item.code} item={item} brand={brand} prices={prices} />
            ))}
          </div>
        )}
        {items.length === 0 && (
          <div tw="text-lg text-gray-500 text-center mt-24 mb-64">
            {i18n.language === "it" ? "Nessun articolo trovato." : "No articles found."}
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
};
